import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});

// Intercepteur : ajoute le token à chaque requête
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('mb_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Intercepteur : gère les 401 (session expirée)
API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('mb_token');
      localStorage.removeItem('mb_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('mb_user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(false);
  const [currentGroupeId, setCurrentGroupeId] = useState(
    () => localStorage.getItem('mb_groupe') || null
  );

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await API.post('/auth/login', { email, password });
      localStorage.setItem('mb_token', data.token);
      localStorage.setItem('mb_user', JSON.stringify(data.user));
      setUser(data.user);
if (data.user.default_groupe_id) {
        localStorage.setItem('mb_groupe', data.user.default_groupe_id);
        setCurrentGroupeId(data.user.default_groupe_id);
      }     
 return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('mb_token');
    localStorage.removeItem('mb_user');
    localStorage.removeItem('mb_groupe');
    setUser(null);
    setCurrentGroupeId(null);
  }, []);

  const switchGroupe = useCallback((groupeId) => {
    localStorage.setItem('mb_groupe', groupeId);
    setCurrentGroupeId(groupeId);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates };
      localStorage.setItem('mb_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Helpers rôles
  const isSuperAdmin = user?.roles?.includes('super_admin');
  const isFranchise = user?.roles?.includes('franchise') || isSuperAdmin;
  const isAnimateur = user?.roles?.includes('animateur') || isFranchise;
  const isMembre = user?.roles?.includes('membre') || isAnimateur;

  return (
    <AuthContext.Provider value={{
      user, loading, currentGroupeId,
      login, logout, switchGroupe, updateUser,
      isSuperAdmin, isFranchise, isAnimateur, isMembre,
      api: API,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans AuthProvider');
  return ctx;
};

export { API };
